import { useState, useEffect, useRef } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import { calculatePaces, EVENTS, parseTimeToSeconds, applyAltitudeAdjustment, formatTime, pickPaceBenchmark } from '../../lib/vdot';
import { isRelayEvent } from '../../lib/relays';
import { resolvePaceTags, renderWorkoutText } from '../../lib/paceTags';
import logoColor from '../../assets/White Border Logo.png';
import { parseRunningMiles, parseTimedIntervalMiles, parseCrossTrainingMiles, parseCrossTrainingMilesRange, formatVolume, formatVolumeRange, paceStringToSeconds } from '../../lib/volumeParser';
import {
  billingIsLocked,
  billingMonthLabel,
  currentBillingMonth,
  compareBillingMonths,
  futureTrainingAccessIsOpenForBillingRows,
  futureBillingMonthsThrough,
} from '../../lib/billing';
import { findBestSplits, buildWeekBars } from '../../lib/workoutSegmentParser';
import { TrainingLogModal } from './TrainingLogModal';

function formatSegmentActual(seg) {
  if (!seg) return '';
  const splits = (seg.splits ?? []).filter(Boolean);
  switch (seg.type) {
    case 'interval': {
      const unit = seg.distUnit ?? 'm';
      const reps = seg.reps ?? splits.length;
      const repDists = Array.from({ length: reps }, (_, i) =>
        (seg.repDistances?.[i] !== undefined && seg.repDistances[i] !== '') ? seg.repDistances[i] : seg.distance
      );
      const allSame = repDists.every(d => String(d) === String(repDists[0]));
      const prefix = allSame
        ? (reps && repDists[0] ? `${reps}×${repDists[0]}${unit}` : seg.label ?? '')
        : repDists.map(d => `${d}${unit}`).join(', ');
      return splits.length ? `${prefix}: ${splits.join(', ')}` : prefix;
    }
    case 'timed_interval': {
      const reps = seg.reps ?? splits.length;
      const dur = seg.duration ? `${seg.duration}s` : '';
      const prefix = reps && dur ? `${reps}×${dur}` : seg.label ?? '';
      return splits.length ? `${prefix}: ${splits.join(', ')}` : prefix;
    }
    case 'continuous': {
      if (!seg.distance) return seg.label ?? '';
      const distLabel = `${seg.distance}${seg.distUnit ?? 'mi'}`;
      if (seg.time) {
        const totalSec = parseTimeToSeconds(seg.time);
        const dist = parseFloat(seg.distance);
        if (totalSec && dist) {
          const miles = (seg.distUnit === 'km') ? dist / 1.60934 : (seg.distUnit === 'm') ? dist / 1609.344 : dist;
          const paceSec = totalSec / miles;
          const pm = Math.floor(paceSec / 60);
          const ps = Math.round(paceSec % 60);
          return `${distLabel} (${pm}:${String(ps).padStart(2, '0')}/mi)`;
        }
      }
      return distLabel;
    }
    case 'checkbox':
      return seg.checked ? `✓ ${seg.label ?? ''}` : (seg.label ?? '');
    case 'xt':
      return seg.duration ? `${seg.duration} ${seg.label || 'XT'}` : (seg.label ?? 'XT');
    default:
      return seg.label ?? '';
  }
}

const GRADE_NUMS = { '6th': 6, '7th': 7, '8th': 8, '9th': 9, '10th': 10, '11th': 11, '12th': 12 };
const NUM_GRADES = { 6: '6th', 7: '7th', 8: '8th', 9: '9th', 10: '10th', 11: '11th', 12: '12th' };

function currentSchoolYear() {
  const today = new Date();
  const cutoff = new Date(today.getFullYear(), 5, 15); // June 15
  return today >= cutoff ? today.getFullYear() : today.getFullYear() - 1;
}

function daysUntil(dateStr) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const race = new Date(dateStr + 'T00:00:00');
  return Math.round((race - today) / (1000 * 60 * 60 * 24));
}

function formatRaceDate(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatShortDate(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function getWeekDates(referenceDate) {
  const d = new Date(referenceDate);
  const day = d.getDay();
  const diff = day === 0 ? 6 : day - 1;
  d.setDate(d.getDate() - diff);
  return Array.from({ length: 7 }, (_, i) => {
    const dt = new Date(d);
    dt.setDate(d.getDate() + i);
    return dt;
  });
}

function toDateStr(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function weekRangeLabel(weekOffset) {
  const ref = new Date();
  ref.setDate(ref.getDate() + weekOffset * 7);
  const days = getWeekDates(ref);
  const fmt = d => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return `${fmt(days[0])} \u2013 ${fmt(days[6])}`;
}

function getEventLabels(eventField) {
  if (!eventField) return '';
  try {
    const parsed = JSON.parse(eventField);
    if (Array.isArray(parsed)) return parsed.map(e => EVENTS[e]?.label ?? e).join(', ');
  } catch {}
  return EVENTS[eventField]?.label ?? eventField;
}

function parseRaceEvents(eventField) {
  if (!eventField) return [];
  try {
    const parsed = JSON.parse(eventField);
    if (Array.isArray(parsed)) return parsed;
  } catch {}
  return [eventField];
}

export function AthleteDashboard() {
  const { profile } = useAuth();
  const location = useLocation();
  const [athleteGradeLevel, setAthleteGradeLevel] = useState(null);
  const [weekWorkouts, setWeekWorkouts] = useState([]);
  const [weekRaceResults, setWeekRaceResults] = useState({});
  const [loading, setLoading] = useState(true);
  const [hasPlan, setHasPlan] = useState(false);
  const [nextRace, setNextRace] = useState(null);
  const [pendingResults, setPendingResults] = useState([]);
  const [resultInputs, setResultInputs] = useState({});
  const [resultSaving, setResultSaving] = useState(null);
  const [resultErrors, setResultErrors] = useState({});

  // Billing
  const [billingLocked, setBillingLocked] = useState(false);
  const [billingPaid, setBillingPaid] = useState(false);
  const [billingMonth, setBillingMonth] = useState('');

  // PDF generation
  const [pdfGenerating, setPdfGenerating] = useState(false);

  // Parents
  const [parents, setParents] = useState([]);
  const [parentEmail, setParentEmail] = useState('');
  const [parentSaving, setParentSaving] = useState(false);
  const [parentError, setParentError] = useState('');

  // Week navigation (0 = current, -1 = last week, etc.)
  const [weekOffset, setWeekOffset] = useState(0);
  const [canGoForward, setCanGoForward] = useState(false);
  const [futureAccessBlocked, setFutureAccessBlocked] = useState(false);

  // Training logs for displayed week
  const [trainingLogs, setTrainingLogs] = useState({});       // { date: mergedLog }
  const [trainingLogSessions, setTrainingLogSessions] = useState({}); // { date: { am, pm, full } }
  const [logModalDay, setLogModalDay] = useState(null);

  // History / chart / best times
  const [historyLogs, setHistoryLogs] = useState([]);
  const [bestSplits, setBestSplits] = useState({});

  // Paces promoted to state so TrainingLogModal can access them
  const [paces, setPaces] = useState(null);

  // Paused account
  const [isPaused, setIsPaused] = useState(false);

  // Coach assignment
  const [coachName, setCoachName] = useState(null);
  const [coachAvatarUrl, setCoachAvatarUrl] = useState(null);
  const [coachBio, setCoachBio] = useState(null);
  const [coachCredentials, setCoachCredentials] = useState(null);

  // Plan day-swap / rest day
  const [planId, setPlanId] = useState(null);
  const [workoutPlanMap, setWorkoutPlanMap] = useState({}); // { date: { id, description, coachNote, athleteRest, swappedFromDate } }
  const [dragOverDate, setDragOverDate] = useState(null);
  const dragDateRef = useRef(null);
  const [swapSaving, setSwapSaving] = useState(false);

  // Vacations
  const [vacations, setVacations] = useState([]);
  const [showVacForm, setShowVacForm] = useState(false);
  const [vacStart, setVacStart] = useState('');
  const [vacEnd, setVacEnd] = useState('');
  const [vacEquipment, setVacEquipment] = useState([]);
  const [vacBlackouts, setVacBlackouts] = useState([]);
  const [vacNotes, setVacNotes] = useState('');
  const [vacSaving, setVacSaving] = useState(false);
  const [vacError, setVacError] = useState('');

  // Onboarding modal
  const [showBaselineModal, setShowBaselineModal] = useState(false);
  const [onbStep, setOnbStep] = useState(1); // 1 = profile, 2 = school/events, 3 = baselines
  // Step 1 — personal
  const [onbGender, setOnbGender] = useState('');
  const [onbBirthDate, setOnbBirthDate] = useState('');
  const [onbPhone, setOnbPhone] = useState('');
  const [onbAddress, setOnbAddress] = useState('');
  const [onbShirtSize, setOnbShirtSize] = useState('');
  // Step 2 — school / events
  const [onbGradeLevel, setOnbGradeLevel] = useState('');
  const [onbSchoolName, setOnbSchoolName] = useState('');
  const [onbEvents, setOnbEvents] = useState([]);
  // Step 3 — baselines
  const [baselineRows, setBaselineRows] = useState([{ event: 'mile', time: '', date: '', customDist: '', customUnit: 'mi' }]);
  const [baselineSaving, setBaselineSaving] = useState(false);
  const [baselineError, setBaselineError] = useState('');

  // Reload week when offset changes
  useEffect(() => {
    if (profile) loadWeek(weekOffset);
  }, [profile, weekOffset]);

  function monthForWeekOffset(offset) {
    const ref = new Date();
    ref.setDate(ref.getDate() + offset * 7);
    const weekDates = getWeekDates(ref);
    return {
      month: weekDates[0].getMonth() + 1,
      year: weekDates[0].getFullYear(),
    };
  }

  function emptyWeek(weekDates, racesMap = {}) {
    const todayStr = toDateStr(new Date());
    return weekDates.map((d, i) => {
      const ds = toDateStr(d);
      return {
        date: ds,
        dayLabel: DAY_LABELS[i],
        dayNum: d.getDate(),
        workout: '',
        rawWorkout: '',
        runMiles: 0,
        xtMiles: 0,
        race: racesMap[ds] ?? null,
        isToday: ds === todayStr,
      };
    });
  }

  async function futureMonthIsOpen(month, year) {
    if (!profile?.id) return false;
    const current = currentBillingMonth();
    if (compareBillingMonths({ month, year }, current) <= 0) {
      return true;
    }

    const { data: assignments } = await supabase
      .from('athlete_plan_assignments')
      .select('plan_id, plans(id, month, year, status)')
      .eq('athlete_id', profile.id);

    let cursor = { month: current.month + 1, year: current.year };
    if (cursor.month > 12) cursor = { month: 1, year: cursor.year + 1 };

    while (compareBillingMonths(cursor, { month, year }) <= 0) {
      const cursorMonth = cursor.month;
      const cursorYear = cursor.year;
      const match = (assignments ?? [])
        .filter(a => a.plans?.month === cursorMonth && a.plans?.year === cursorYear)
        .reduce((best, a) => (!best || a.plans.id > best.plans.id ? a : best), null);
      const { data: billingRows } = await supabase
        .from('billing')
        .select('paid_at')
        .eq('athlete_id', profile.id)
        .eq('month', cursorMonth)
        .eq('year', cursorYear);

      if (!futureTrainingAccessIsOpenForBillingRows(match?.plans, billingRows)) return false;

      cursor = { month: cursor.month + 1, year: cursor.year };
      if (cursor.month > 12) cursor = { month: 1, year: cursor.year + 1 };
    }

    return true;
  }

  async function updateForwardAccess(offset) {
    const target = monthForWeekOffset(offset + 1);
    setCanGoForward(await futureMonthIsOpen(target.month, target.year));
  }

  // One-time loads on mount
  useEffect(() => {
    if (profile) {
      loadNextRace();
      loadPendingResults();
      loadParents();
      loadHistoryLogs();
      loadVacations();
      checkBaseline();
      checkGradeRollover();
      loadCoachProfile();
    }
  }, [profile]);

  async function checkGradeRollover() {
    const { data: ap } = await supabase
      .from('athlete_profiles')
      .select('grade_level, grade_set_year')
      .eq('id', profile.id)
      .maybeSingle();
    if (!ap?.grade_level || !ap?.grade_set_year || !(ap.grade_level in GRADE_NUMS)) return;
    const schoolYear = currentSchoolYear();
    const yearsElapsed = schoolYear - ap.grade_set_year;
    if (yearsElapsed <= 0) return;
    const newNum = GRADE_NUMS[ap.grade_level] + yearsElapsed;
    if (newNum > 12 || !NUM_GRADES[newNum]) return; // graduated
    await supabase.from('athlete_profiles').upsert({
      id: profile.id,
      grade_level: NUM_GRADES[newNum],
      grade_set_year: schoolYear,
    });
  }

  async function checkBaseline() {
    const { data } = await supabase
      .from('athlete_profiles')
      .select('baseline_set, paused, grade_level')
      .eq('id', profile.id)
      .maybeSingle();
    // Pro athletes get redirected to /pro/dashboard; skip modal
    if (data?.grade_level === 'pro') {
      setAthleteGradeLevel('pro');
      return;
    }
    if (data?.paused) {
      setIsPaused(true);
      return;
    }
    // Show modal if no row exists yet (new signup) or baseline not yet completed
    if (!data || data.baseline_set !== true) {
      setShowBaselineModal(true);
    }
  }

  async function handleBaselineSubmit() {
    setBaselineError('');
    setBaselineSaving(true);

    // Save profile fields
    const profileUpdate = { baseline_set: true };
    if (onbGender) profileUpdate.gender = onbGender;
    if (onbBirthDate) profileUpdate.birth_date = onbBirthDate;
    if (onbPhone) profileUpdate.phone = onbPhone;
    if (onbAddress) profileUpdate.address = onbAddress;
    if (onbShirtSize) profileUpdate.shirt_size = onbShirtSize;
    if (onbGradeLevel) {
      profileUpdate.grade_level = onbGradeLevel;
      if (onbGradeLevel in GRADE_NUMS) profileUpdate.grade_set_year = currentSchoolYear();
    }
    if (onbSchoolName) profileUpdate.school_name = onbSchoolName;
    if (onbEvents.length > 0) profileUpdate.events = onbEvents;
    const { error: upsertError } = await supabase.from('athlete_profiles').upsert({ id: profile.id, ...profileUpdate });
    if (upsertError) {
      setBaselineError('Could not save your profile. Please try again.');
      setBaselineSaving(false);
      return;
    }

    // Save baseline benchmarks
    const valid = baselineRows.filter(r => r.time.trim() && r.date.trim());
    for (const row of valid) {
      const seconds = parseTimeToSeconds(row.time);
      if (!seconds) continue;
      await supabase.from('benchmarks').insert({
        athlete_id: profile.id,
        event: row.event === 'other'
          ? (row.customDist ? `${parseFloat(row.customDist).toFixed(1)} ${row.customUnit}` : row.event)
          : row.event,
        time_seconds: seconds,
        race_date: row.date,
        is_baseline: true,
        notes: 'Baseline entry',
      });
    }

    setBaselineSaving(false);
    setShowBaselineModal(false);
  }

  function handleBaselineSkip() {
    setShowBaselineModal(false);
  }

  function toggleOnbEvent(key) {
    setOnbEvents(prev => prev.includes(key) ? prev.filter(e => e !== key) : [...prev, key]);
  }

  function onbSchoolLabel() {
    if (['6th','7th','8th'].includes(onbGradeLevel)) return 'Middle School Name';
    if (['9th','10th','11th','12th'].includes(onbGradeLevel)) return 'High School Name';
    if (onbGradeLevel === 'collegiate') return 'College / University';
    return null;
  }

  // Recompute best splits when history changes
  useEffect(() => {
    setBestSplits(findBestSplits(historyLogs));
  }, [historyLogs]);

  async function loadNextRace() {
    const today = new Date().toISOString().split('T')[0];
    const { data } = await supabase
      .from('races')
      .select('*')
      .eq('athlete_id', profile.id)
      .gte('race_date', today)
      .order('race_date', { ascending: true })
      .limit(1);
    setNextRace(data?.[0] ?? null);
  }

  async function loadPendingResults() {
    const today = new Date().toISOString().split('T')[0];
    const { data } = await supabase
      .from('races')
      .select('*')
      .eq('athlete_id', profile.id)
      .lt('race_date', today)
      .is('result_time_seconds', null)
      .order('race_date', { ascending: false })
      .limit(5);
    setPendingResults(data ?? []);
  }

  async function handleLogResult(race) {
    const timeStr = resultInputs[race.id] ?? '';
    const seconds = parseTimeToSeconds(timeStr);
    if (!seconds) {
      setResultErrors(prev => ({ ...prev, [race.id]: 'Enter a valid time (e.g. 4:32.56 or 16:45.00)' }));
      return;
    }
    setResultErrors(prev => ({ ...prev, [race.id]: '' }));
    setResultSaving(race.id);
    await supabase.from('races').update({ result_time_seconds: seconds }).eq('id', race.id);
    const events = parseRaceEvents(race.event);
    await supabase.from('benchmarks').insert({
      athlete_id: profile.id,
      event: events[0] ?? race.event,
      time_seconds: seconds,
      race_date: race.race_date,
      notes: race.name,
    });
    setResultSaving(null);
    setResultInputs(prev => ({ ...prev, [race.id]: '' }));
    await loadPendingResults();
    await loadNextRace();
  }

  async function loadCoachProfile() {
    // Get this athlete's coach_id
    const { data: ap } = await supabase
      .from('athlete_profiles')
      .select('coach_id')
      .eq('id', profile.id)
      .maybeSingle();
    if (!ap?.coach_id) return;
    // Get coach name + profile extras
    const [{ data: coachProf }, { data: coachExtra }] = await Promise.all([
      supabase.from('profiles').select('full_name').eq('id', ap.coach_id).single(),
      supabase.from('coach_profiles').select('avatar_url, bio, credentials').eq('id', ap.coach_id).maybeSingle(),
    ]);
    if (coachProf?.full_name) setCoachName(coachProf.full_name);
    if (coachExtra?.avatar_url) setCoachAvatarUrl(coachExtra.avatar_url);
    if (coachExtra?.bio) setCoachBio(coachExtra.bio);
    if (coachExtra?.credentials) setCoachCredentials(coachExtra.credentials);
  }

  async function loadParents() {
    const { data } = await supabase
      .from('parent_athlete_links')
      .select('id, parent_id, profiles!parent_athlete_links_parent_id_fkey(full_name, email)')
      .eq('athlete_id', profile.id);
    setParents(data ?? []);
  }

  async function handleAddParent(e) {
    e.preventDefault();
    setParentError('');
    setParentSaving(true);
    const { data: found } = await supabase
      .from('profiles')
      .select('id')
      .eq('email', parentEmail.trim().toLowerCase())
      .eq('role', 'parent')
      .maybeSingle();
    if (!found) {
      setParentError('No parent account found with that email. They must sign up first with role "parent".');
      setParentSaving(false);
      return;
    }
    const already = parents.find(p => p.parent_id === found.id);
    if (already) {
      setParentError('This parent is already linked.');
      setParentSaving(false);
      return;
    }
    const { error: err } = await supabase.from('parent_athlete_links').insert({
      parent_id: found.id,
      athlete_id: profile.id,
    });
    if (err) { setParentError(err.message); setParentSaving(false); return; }
    setParentEmail('');
    await loadParents();
    setParentSaving(false);
  }

  async function handleRemoveParent(linkId) {
    await supabase.from('parent_athlete_links').delete().eq('id', linkId);
    setParents(prev => prev.filter(p => p.id !== linkId));
  }

  async function loadVacations() {
    const today = new Date().toISOString().split('T')[0];
    const { data } = await supabase
      .from('athlete_vacations')
      .select('id, start_date, end_date, available_equipment, blackout_dates, notes')
      .eq('athlete_id', profile.id)
      .gte('end_date', today)
      .order('start_date', { ascending: true });
    setVacations(data ?? []);
  }

  async function handleAddVacation(e) {
    e.preventDefault();
    setVacError('');
    if (!vacStart || !vacEnd) { setVacError('Please enter start and end dates.'); return; }
    if (vacEnd < vacStart) { setVacError('End date must be on or after start date.'); return; }
    setVacSaving(true);
    const { error: err } = await supabase.from('athlete_vacations').insert({
      athlete_id: profile.id,
      start_date: vacStart,
      end_date: vacEnd,
      available_equipment: vacEquipment,
      blackout_dates: vacBlackouts,
      notes: vacNotes.trim() || null,
    });
    if (err) { setVacError(err.message); setVacSaving(false); return; }
    setVacStart('');
    setVacEnd('');
    setVacEquipment([]);
    setVacBlackouts([]);
    setVacNotes('');
    setShowVacForm(false);
    await loadVacations();
    setVacSaving(false);
  }

  async function handleDeleteVacation(vacId) {
    await supabase.from('athlete_vacations').delete().eq('id', vacId);
    setVacations(prev => prev.filter(v => v.id !== vacId));
  }

  async function loadHistoryLogs() {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - 112); // 16 weeks back
    const { data } = await supabase
      .from('training_logs')
      .select('log_date, segments, total_miles')
      .eq('athlete_id', profile.id)
      .gte('log_date', toDateStr(cutoff))
      .order('log_date', { ascending: true });
    setHistoryLogs(data ?? []);
  }

  function handleLogSaved(logData) {
    const { log_date, session } = logData;
    const sess = session ?? 'full';
    // Update sessions map
    setTrainingLogSessions(prev => ({
      ...prev,
      [log_date]: { ...(prev[log_date] ?? {}), [sess]: logData },
    }));
    // Rebuild merged entry for this date
    setTrainingLogs(prev => {
      const allSessions = { ...(prev[log_date]?._sessions ?? {}), [sess]: logData };
      const merged = Object.values(allSessions).reduce((acc, l) => ({
        segments: [...acc.segments, ...(l.segments ?? [])],
        total_miles: acc.total_miles + (l.total_miles ?? 0),
        running_miles: acc.running_miles + (l.running_miles ?? l.total_miles ?? 0),
        xt_miles: acc.xt_miles + (l.xt_miles ?? 0),
        notes: l.notes ?? acc.notes,
        _sessions: allSessions,
      }), { segments: [], total_miles: 0, running_miles: 0, xt_miles: 0, notes: '', _sessions: allSessions });
      return { ...prev, [log_date]: merged };
    });
    setHistoryLogs(prev => {
      const without = prev.filter(l => l.log_date !== log_date);
      const merged = { log_date, segments: logData.segments, total_miles: logData.total_miles };
      return [...without, merged].sort((a, b) => a.log_date < b.log_date ? -1 : 1);
    });
  }

  async function loadWeek(offset = 0) {
    setLoading(true);
    const ref = new Date();
    ref.setDate(ref.getDate() + offset * 7);
    const weekDates = getWeekDates(ref);
    const today = new Date();
    const todayStr = toDateStr(today);
    const dateStrings = weekDates.map(toDateStr);
    const refMonth = weekDates[0].getMonth() + 1;
    const refYear = weekDates[0].getFullYear();
    const isFutureMonth = compareBillingMonths(
      { month: refMonth, year: refYear },
      currentBillingMonth()
    ) > 0;

    if (isFutureMonth && !(await futureMonthIsOpen(refMonth, refYear))) {
      setBillingLocked(false);
      setFutureAccessBlocked(true);
      setHasPlan(false);
      setPlanId(null);
      setWorkoutPlanMap({});
      setTrainingLogs({});
      setTrainingLogSessions({});
      setWeekWorkouts(emptyWeek(weekDates));
      await updateForwardAccess(offset);
      setLoading(false);
      return;
    }

    setFutureAccessBlocked(false);

    // Billing: only check for the current week
    if (offset === 0) {
      const { data: billing } = await supabase
        .from('billing')
        .select('paid_at, month, year')
        .eq('athlete_id', profile.id)
        .eq('month', today.getMonth() + 1)
        .eq('year', today.getFullYear())
        .maybeSingle();
      setBillingLocked(billingIsLocked(billing));
      setBillingPaid(!!billing?.paid_at);
      setBillingMonth(billingMonthLabel(today.getMonth() + 1, today.getFullYear()));
    } else {
      setBillingLocked(false);
    }

    // Load training logs for displayed week
    const { data: logData } = await supabase
      .from('training_logs')
      .select('id, log_date, segments, total_miles, running_miles, xt_miles, notes, session')
      .eq('athlete_id', profile.id)
      .in('log_date', dateStrings);
    // Build sessions dict first (deduplicate by keeping highest-ID row per date+session)
    const sessions = {};
    for (const row of logData ?? []) {
      const date = row.log_date;
      const sess = row.session ?? 'full';
      if (!sessions[date]) sessions[date] = {};
      const existing = sessions[date][sess];
      if (!existing || row.id > existing.id) sessions[date][sess] = row;
    }
    // Build merged from deduped sessions only
    const merged = {};
    for (const [date, dateSessions] of Object.entries(sessions)) {
      for (const row of Object.values(dateSessions)) {
        if (!merged[date]) merged[date] = { segments: [], total_miles: 0, running_miles: 0, xt_miles: 0, notes: row.notes, _sessions: dateSessions };
        merged[date].segments = [...merged[date].segments, ...(row.segments ?? [])];
        merged[date].total_miles += row.total_miles ?? 0;
        merged[date].running_miles += row.running_miles ?? row.total_miles ?? 0;
        merged[date].xt_miles += row.xt_miles ?? 0;
      }
    }
    setTrainingLogs(merged);
    setTrainingLogSessions(sessions);

    // Load races falling within this week
    const { data: weekRaces } = await supabase
      .from('races')
      .select('id, name, event, race_date, priority, result_time_seconds')
      .eq('athlete_id', profile.id)
      .in('race_date', dateStrings);
    const weekRacesArr = weekRaces ?? [];
    const racesMap = {};
    weekRacesArr.forEach(r => { racesMap[r.race_date] = r; });

    // Fetch benchmarks for multi-event race results
    const raceResultsMap = {};
    if (weekRacesArr.length > 0) {
      const raceDates = weekRacesArr.map(r => r.race_date);
      const { data: raceBenchmarks } = await supabase
        .from('benchmarks')
        .select('event, time_seconds, race_date, notes')
        .eq('athlete_id', profile.id)
        .in('race_date', raceDates);
      weekRacesArr.forEach(race => {
        parseRaceEvents(race.event).forEach(ek => {
          const bm =
            (raceBenchmarks ?? []).find(b => b.event === ek && b.race_date === race.race_date && b.notes === race.name) ||
            (raceBenchmarks ?? []).find(b => b.event === ek && b.race_date === race.race_date);
          if (bm) raceResultsMap[`${race.id}-${ek}`] = formatTime(bm.time_seconds);
        });
      });
    }
    setWeekRaceResults(raceResultsMap);

    const { data: assignments } = await supabase
      .from('athlete_plan_assignments')
      .select('plan_id, plans(id, month, year, status)')
      .eq('athlete_id', profile.id);

    const accessCache = {};
    async function monthIsOpen(month, year) {
      const key = `${year}-${month}`;
      if (accessCache[key] != null) return accessCache[key];
      for (const futureMonth of futureBillingMonthsThrough(month, year)) {
        const futureMatch = (assignments ?? [])
          .filter(a => a.plans?.month === futureMonth.month && a.plans?.year === futureMonth.year)
          .reduce((best, a) => !best || a.plans.id > best.plans.id ? a : best, null);
        const { data: billingRows } = await supabase
          .from('billing')
          .select('paid_at')
          .eq('athlete_id', profile.id)
          .eq('month', futureMonth.month)
          .eq('year', futureMonth.year);
        if (!futureTrainingAccessIsOpenForBillingRows(futureMatch?.plans, billingRows)) {
          accessCache[key] = false;
          return false;
        }
      }
      accessCache[key] = true;
      return true;
    }

    const planByDate = {};
    for (const d of weekDates) {
      const month = d.getMonth() + 1;
      const year = d.getFullYear();
      if (!(await monthIsOpen(month, year))) continue;
      const match = (assignments ?? [])
        .filter(a => a.plans?.month === month && a.plans?.year === year)
        .reduce((best, a) => !best || a.plans.id > best.plans.id ? a : best, null);
      if (match?.plans?.id) planByDate[toDateStr(d)] = match.plans.id;
    }

    const planIds = [...new Set(Object.values(planByDate))];
    if (!planIds.length) {
      setHasPlan(false);
      setPlanId(null);
      setWorkoutPlanMap({});
      setWeekWorkouts(emptyWeek(weekDates, racesMap));
      await updateForwardAccess(offset);
      setLoading(false);
      return;
    }

    setHasPlan(true);

    setPlanId(planIds[0]);

    const { data: planWorkouts } = await supabase
      .from('plan_workouts')
      .select('id, plan_id, workout_date, custom_description, coach_notes, athlete_rest, swapped_from_date')
      .in('plan_id', planIds)
      .in('workout_date', dateStrings);

    const workoutMap = {};
    const coachNoteMap = {};
    const planMap = {};
    (planWorkouts ?? []).forEach(w => {
      if (planByDate[w.workout_date] !== w.plan_id) return;
      workoutMap[w.workout_date] = w.custom_description ?? '';
      if (w.coach_notes) coachNoteMap[w.workout_date] = w.coach_notes;
      planMap[w.workout_date] = {
        id: w.id,
        description: w.custom_description ?? '',
        coachNote: w.coach_notes ?? null,
        athleteRest: w.athlete_rest ?? false,
        swappedFromDate: w.swapped_from_date ?? null,
      };
    });
    setWorkoutPlanMap(planMap);

    const [{ data: benchmarks }, { data: apAlt }] = await Promise.all([
      supabase.from('benchmarks').select('id, event, time_seconds')
        .eq('athlete_id', profile.id).order('race_date', { ascending: false }).limit(20),
      supabase.from('athlete_profiles').select('training_altitude_ft, pinned_benchmark_id')
        .eq('id', profile.id).maybeSingle(),
    ]);

    let localPaces = null;
    let localEasyPaceSec = null;
    const bench = pickPaceBenchmark(benchmarks, apAlt?.pinned_benchmark_id);
    if (bench) {
      const result = calculatePaces(bench.event, bench.time_seconds);
      localPaces = result?.paces ?? null;
      if (localPaces) localPaces = applyAltitudeAdjustment(localPaces, apAlt?.training_altitude_ft ?? null);
      if (localPaces?.easy?.perMile) localEasyPaceSec = paceStringToSeconds(localPaces.easy.perMile);
    }
    setPaces(localPaces);

    setWeekWorkouts(weekDates.map((d, i) => {
      const dateStr = toDateStr(d);
      const rawText = workoutMap[dateStr] ?? '';
      const resolvedText = localPaces ? resolvePaceTags(rawText, localPaces) : rawText;
      const runMiles = parseRunningMiles(rawText) + parseTimedIntervalMiles(rawText, localPaces);
      const xtMiles = parseCrossTrainingMiles(rawText, localEasyPaceSec);
      const xtRange = localEasyPaceSec ? parseCrossTrainingMilesRange(rawText, localEasyPaceSec) : null;
      return {
        date: dateStr,
        dayLabel: DAY_LABELS[i],
        dayNum: d.getDate(),
        workout: resolvedText,
        rawWorkout: rawText,
        coachNote: coachNoteMap[dateStr] ?? '',
        runMiles,
        xtMiles,
        xtRange,
        race: racesMap[dateStr] ?? null,
        isToday: dateStr === todayStr,
        athleteRest: planMap[dateStr]?.athleteRest ?? false,
        swappedFromDate: planMap[dateStr]?.swappedFromDate ?? null,
      };
    }));

    await updateForwardAccess(offset);
    setLoading(false);
  }

  // ── Day swap ──────────────────────────────────────────────────────
  async function handleSwapDays(dateA, dateB) {
    if (!planId || dateA === dateB || swapSaving) return;
    setSwapSaving(true);

    const wA = workoutPlanMap[dateA];
    const wB = workoutPlanMap[dateB];
    const descA = wA?.description ?? '';
    const descB = wB?.description ?? '';
    const noteA = wA?.coachNote ?? null;
    const noteB = wB?.coachNote ?? null;
    const restA = wA?.athleteRest ?? false;
    const restB = wB?.athleteRest ?? false;

    if (!descA && !descB && !restA && !restB) { setSwapSaving(false); return; }

    // Detect if this is a "return to original" swap:
    // wB was previously moved FROM dateA → dateB (swappedFromDate === dateA), or vice versa.
    const isUndo = wB?.swappedFromDate === dateA || wA?.swappedFromDate === dateB;

    // Write B's content into A — clear swappedFromDate if returning home, else mark as moved
    const newSwapForA = isUndo ? null : ((descB || restB) ? dateB : null);
    if (wA?.id) {
      await supabase.from('plan_workouts').update({
        custom_description: descB || null, coach_notes: noteB,
        athlete_rest: restB, swapped_from_date: newSwapForA,
      }).eq('id', wA.id);
    } else if (descB || restB) {
      await supabase.from('plan_workouts').insert({
        plan_id: planId, workout_date: dateA,
        custom_description: descB || null, coach_notes: noteB,
        athlete_rest: restB, swapped_from_date: newSwapForA,
      });
    }

    // Write A's content into B
    const newSwapForB = isUndo ? null : ((descA || restA) ? dateA : null);
    if (wB?.id) {
      await supabase.from('plan_workouts').update({
        custom_description: descA || null, coach_notes: noteA,
        athlete_rest: restA, swapped_from_date: newSwapForB,
      }).eq('id', wB.id);
    } else if (descA || restA) {
      await supabase.from('plan_workouts').insert({
        plan_id: planId, workout_date: dateB,
        custom_description: descA || null, coach_notes: noteA,
        athlete_rest: restA, swapped_from_date: newSwapForB,
      });
    }

    if (isUndo) {
      // Returning to original — dismiss any open notifications for this swap pair
      await supabase.from('athlete_plan_notifications')
        .update({ coach_reviewed: true })
        .eq('athlete_id', profile.id)
        .eq('type', 'day_swap')
        .or(`and(original_date.eq.${dateA},new_date.eq.${dateB}),and(original_date.eq.${dateB},new_date.eq.${dateA})`);
    } else {
      // Notify coach of the swap
      const labelA = weekWorkouts.find(d => d.date === dateA)?.dayLabel ?? dateA;
      const labelB = weekWorkouts.find(d => d.date === dateB)?.dayLabel ?? dateB;
      await supabase.from('athlete_plan_notifications').insert({
        plan_id: planId, athlete_id: profile.id, type: 'day_swap',
        original_date: dateA, new_date: dateB,
        message: `Swapped ${labelA} (${dateA}) ↔ ${labelB} (${dateB})`,
      });
    }

    setSwapSaving(false);
    await loadWeek(weekOffset);
  }

  async function handleRestDay(date, isRest) {
    if (!planId) return;
    const w = workoutPlanMap[date];
    if (w?.id) {
      await supabase.from('plan_workouts').update({ athlete_rest: isRest }).eq('id', w.id);
    } else {
      await supabase.from('plan_workouts').insert({
        plan_id: planId, workout_date: date, athlete_rest: isRest, custom_description: null,
      });
    }
    if (isRest) {
      await supabase.from('athlete_plan_notifications').insert({
        plan_id: planId, athlete_id: profile.id, type: 'rest_day',
        original_date: date, message: `Took rest day on ${date}`,
      });
    }
    // Update local state without full reload
    setWorkoutPlanMap(prev => ({
      ...prev,
      [date]: { ...(prev[date] ?? { id: null, description: '', coachNote: null, swappedFromDate: null }), athleteRest: isRest },
    }));
    setWeekWorkouts(prev => prev.map(d => d.date === date ? { ...d, athleteRest: isRest } : d));
  }

  // ── Monthly plan PDF download ────────────────────────────────────
  async function downloadMonthlyPlanPDF() {
    setPdfGenerating(true);
    try {
      const { jsPDF } = await import('jspdf');

      const today = new Date();
      const month = today.getMonth() + 1;
      const year = today.getFullYear();
      const monthName = new Date(year, month - 1, 1)
        .toLocaleString('en-US', { month: 'long', year: 'numeric' });

      // Fetch this month's plan assignment
      const { data: assignments } = await supabase
        .from('athlete_plan_assignments')
        .select('plan_id, plans(id, month, year)')
        .eq('athlete_id', profile.id);
      const match = assignments?.find(a => a.plans?.month === month && a.plans?.year === year);
      if (!match) { setPdfGenerating(false); return; }

      const lastDay = new Date(year, month, 0).getDate();
      const monthStr = String(month).padStart(2, '0');
      const start = `${year}-${monthStr}-01`;
      const end   = `${year}-${monthStr}-${String(lastDay).padStart(2, '0')}`;

      const { data: workouts } = await supabase
        .from('plan_workouts')
        .select('workout_date, custom_description')
        .eq('plan_id', match.plans.id)
        .gte('workout_date', start)
        .lte('workout_date', end);

      const rawMap = {};
      (workouts ?? []).forEach(w => { rawMap[w.workout_date] = w.custom_description ?? ''; });

      // Resolve pace tags for every day
      const resolvedMap = {};
      for (const [date, text] of Object.entries(rawMap)) {
        resolvedMap[date] = resolvePaceTags(text, paces);
      }

      // Build calendar cells (Mon–Sun grid)
      // getDay() returns 0=Sun…6=Sat; convert to Mon-based offset (Mon=0…Sun=6)
      const firstDow = (new Date(year, month - 1, 1).getDay() + 6) % 7;
      const cells = [];
      for (let i = 0; i < firstDow; i++) cells.push(null);
      for (let d = 1; d <= lastDay; d++) {
        const dateStr = `${year}-${monthStr}-${String(d).padStart(2, '0')}`;
        cells.push({ day: d, date: dateStr, workout: resolvedMap[dateStr] || '' });
      }
      while (cells.length % 7 !== 0) cells.push(null);
      const weeks = [];
      for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));

      // ── PDF layout constants (mm, letter landscape 279.4 × 215.9) ──
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'letter' });
      const PW = 279.4;
      const PH = 215.9;
      const ML = 10, MR = 10;
      const CW = PW - ML - MR;          // 259.4mm
      const COL_W = CW / 7;             // ~37mm per column

      const NAVY  = [15, 23, 42];
      const GOLD  = [180, 143, 60];
      const GRAY_BG   = [247, 248, 250];
      const GRAY_LINE = [210, 214, 220];
      const GRAY_TEXT = [150, 155, 165];
      const TODAY_BG  = [255, 252, 235];

      const HDR_H = 22;
      const DAY_HDR_H = 6;
      const FOOTER_H = 10;
      const CAL_Y = HDR_H + DAY_HDR_H;
      const CAL_H = PH - CAL_Y - FOOTER_H;
      const ROW_H = CAL_H / weeks.length;

      const CELL_PAD = 1.0;
      const NUM_H    = 3.5;   // height reserved for day number
      const LINE_H   = 2.2;   // line height at fontSize 5.5
      const FONT_SZ  = 5.5;
      const maxLines = Math.floor((ROW_H - NUM_H - CELL_PAD * 2) / LINE_H);

      // ── Header ────────────────────────────────────────────────────
      doc.setFillColor(...NAVY);
      doc.rect(0, 0, PW, HDR_H, 'F');

      // Logo — load via HTMLImageElement + canvas to get a data URL jsPDF can embed
      try {
        const { dataUrl: logoDataUrl, imgW, imgH } = await new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = () => {
            // Cap canvas to 600×600 px to avoid "Invalid string length" on high-res PNGs
            const MAX_PX = 600;
            const scale = Math.min(MAX_PX / img.naturalWidth, MAX_PX / img.naturalHeight, 1);
            const canvas = document.createElement('canvas');
            canvas.width = Math.round(img.naturalWidth * scale);
            canvas.height = Math.round(img.naturalHeight * scale);
            canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
            resolve({ dataUrl: canvas.toDataURL('image/png'), imgW: img.naturalWidth, imgH: img.naturalHeight });
          };
          img.onerror = reject;
          img.src = logoColor;
        });
        // Fit logo into a max 44mm wide, 17mm tall box while preserving aspect ratio
        const MAX_LOGO_W = 44, MAX_LOGO_H = 17;
        const ratio = imgW / imgH;
        let LOGO_W = MAX_LOGO_W;
        let LOGO_H = LOGO_W / ratio;
        if (LOGO_H > MAX_LOGO_H) { LOGO_H = MAX_LOGO_H; LOGO_W = LOGO_H * ratio; }
        doc.addImage(logoDataUrl, 'PNG', ML, (HDR_H - LOGO_H) / 2, LOGO_W, LOGO_H);
      } catch {
        // Fallback text if logo fails
        doc.setTextColor(255, 255, 255);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(14);
        doc.text('APEX ELITE', ML, 11);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.text('MONTHLY TRAINING PLAN', ML, 17);
      }

      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.text(monthName.toUpperCase(), PW - MR, 11, { align: 'right' });
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8.5);
      doc.text(profile.full_name ?? '', PW - MR, 17, { align: 'right' });

      // Gold separator
      doc.setDrawColor(...GOLD);
      doc.setLineWidth(0.6);
      doc.line(0, HDR_H, PW, HDR_H);

      // ── Day column headers ────────────────────────────────────────
      const DAY_NAMES = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];
      doc.setFillColor(...GRAY_BG);
      doc.rect(ML, HDR_H, CW, DAY_HDR_H, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(6.5);
      doc.setTextColor(...NAVY);
      DAY_NAMES.forEach((name, i) => {
        doc.text(name, ML + i * COL_W + COL_W / 2, HDR_H + 4.7, { align: 'center' });
      });

      // ── Calendar grid ─────────────────────────────────────────────
      const todayStr = toDateStr(today);
      doc.setLineWidth(0.2);

      weeks.forEach((week, wi) => {
        const rowY = CAL_Y + wi * ROW_H;

        week.forEach((cell, ci) => {
          const cellX = ML + ci * COL_W;

          // Background
          if (!cell) {
            doc.setFillColor(...GRAY_BG);
            doc.rect(cellX, rowY, COL_W, ROW_H, 'F');
          } else if (cell.date === todayStr) {
            doc.setFillColor(...TODAY_BG);
            doc.rect(cellX, rowY, COL_W, ROW_H, 'F');
            // Gold left accent
            doc.setDrawColor(...GOLD);
            doc.setLineWidth(1);
            doc.line(cellX, rowY, cellX, rowY + ROW_H);
            doc.setLineWidth(0.2);
          }

          // Cell border
          doc.setDrawColor(...GRAY_LINE);
          doc.setLineWidth(0.2);
          doc.rect(cellX, rowY, COL_W, ROW_H, 'S');

          if (!cell) return;

          // Day number
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(8);
          doc.setTextColor(...NAVY);
          doc.text(String(cell.day), cellX + CELL_PAD, rowY + NUM_H);

          // Workout text
          const textY = rowY + NUM_H + LINE_H;
          if (cell.workout) {
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(FONT_SZ);
            doc.setTextColor(40, 45, 55);
            const allLines = doc.splitTextToSize(cell.workout, COL_W - CELL_PAD * 2);
            const visibleLines = allLines.slice(0, maxLines);
            if (allLines.length > maxLines) {
              visibleLines[maxLines - 1] = visibleLines[maxLines - 1].replace(/\s*\S+$/, '') + '…';
            }
            doc.text(visibleLines, cellX + CELL_PAD, textY);
          } else {
            doc.setFont('helvetica', 'italic');
            doc.setFontSize(6.5);
            doc.setTextColor(...GRAY_TEXT);
            doc.text('Rest', cellX + CELL_PAD, textY);
          }
        });
      });

      // Bottom border of calendar
      doc.setDrawColor(...NAVY);
      doc.setLineWidth(0.4);
      const calBottom = CAL_Y + weeks.length * ROW_H;
      doc.line(ML, calBottom, ML + CW, calBottom);

      // ── Footer ────────────────────────────────────────────────────
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6.5);
      doc.setTextColor(...GRAY_TEXT);
      const genDate = today.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
      doc.text(`Generated ${genDate} · Apex Elite`, ML, PH - 4);
      doc.text(paces ? 'Paces calibrated to Apex Elite proprietary training zones' : 'No benchmark — raw pace tags shown', PW - MR, PH - 4, { align: 'right' });

      doc.save(`apex-elite-${year}-${monthStr}.pdf`);
    } catch (err) {
      console.error('PDF generation failed:', err);
      alert(`PDF generation failed: ${err?.message ?? err}. Check browser console for details.`);
    } finally {
      setPdfGenerating(false);
    }
  }

  // ── Derived chart data ───────────────────────────────────────────
  const weekBars = buildWeekBars(historyLogs);
  const maxBarMiles = Math.max(...weekBars.map(w => w.miles), 1);


  const todayStr = toDateStr(new Date());

  // ── Actual logged volume totals ──────────────────────────────────
  const actualWeekMiles = Object.values(trainingLogs).reduce((s, l) => s + (l.total_miles || 0), 0);
  const actualTodayMiles = weekOffset === 0 ? (trainingLogs[todayStr]?.total_miles || 0) : 0;
  const currentMonthStr = todayStr.slice(0, 7);
  const actualMonthMiles = historyLogs
    .filter(l => l.log_date.startsWith(currentMonthStr))
    .reduce((s, l) => s + (l.total_miles || 0), 0);

  const weekTitle = weekOffset === 0
    ? "This Week's Training"
    : weekOffset === -1 ? 'Last Week'
    : `Week of ${weekRangeLabel(weekOffset).split(' \u2013')[0]}`;

  // Redirect pro athletes from /athlete/dashboard to /pro/dashboard
  if (athleteGradeLevel === 'pro' && location.pathname === '/athlete/dashboard') {
    return <Navigate to="/pro/dashboard" replace />;
  }

  if (isPaused) {
    return (
      <Layout>
        <div className="empty-state" style={{ marginTop: 80 }}>
          <p className="empty-state-title">Account Paused</p>
          <p className="empty-state-sub">
            Your account has been temporarily paused by your coach. Please reach out to your coach to be reactivated.
          </p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={{ marginBottom: 24 }}>
        <p className="page-eyebrow">Athlete Dashboard</p>
        <h1 className="page-title">Welcome back, {profile?.full_name?.split(' ')[0]}.</h1>
      </div>

      {/* ── Coach assignment ─────────────────────────────── */}
      {coachName && (
        <div className="section-card" style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 16, padding: '14px 20px' }}>
          <div className="profile-avatar" style={{ width: 52, height: 52, fontSize: 16, flexShrink: 0, cursor: 'default' }}>
            {coachAvatarUrl
              ? <img src={coachAvatarUrl} alt={coachName} />
              : coachName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
            }
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--navy)' }}>
              Coach {coachName.split(' ').slice(-1)[0]}
              {coachCredentials && (
                <span style={{ fontWeight: 400, fontSize: 12, color: 'var(--gray-400)', marginLeft: 8 }}>{coachCredentials}</span>
              )}
            </div>
            {coachBio && (
              <div style={{ fontSize: 13, color: 'var(--gray-500)', marginTop: 2 }}>{coachBio}</div>
            )}
          </div>
        </div>
      )}

      {/* ── Next race ────────────────────────────────────── */}
      {nextRace && (
        <div className="section-card" style={{ marginBottom: 20 }}>
          <div className="section-card-header">
            <span className="section-card-title">Next Race</span>
            <Link to="/athlete/races" style={{ fontSize: 12, color: 'var(--gold)', textDecoration: 'none', fontWeight: 600 }}>
              View All
            </Link>
          </div>
          <div style={{ padding: '16px 24px', display: 'flex', alignItems: 'center', gap: 16 }}>
            <div className={`race-priority-badge priority-${nextRace.priority}`} style={{ fontSize: 16, width: 36, height: 36 }}>
              {nextRace.priority}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--navy)' }}>{nextRace.name}</div>
              <div className="cell-muted" style={{ fontSize: 13 }}>
                {getEventLabels(nextRace.event)}
                {nextRace.location && ` · ${nextRace.location}`}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 13, color: 'var(--gray-600)' }}>{formatRaceDate(nextRace.race_date)}</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: daysUntil(nextRace.race_date) <= 7 ? 'var(--gold)' : 'var(--navy)' }}>
                {daysUntil(nextRace.race_date) === 0 ? 'Today' : daysUntil(nextRace.race_date) === 1 ? 'Tomorrow' : `${daysUntil(nextRace.race_date)} days away`}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Weekly training ──────────────────────────────── */}
      <div className="section-card">
        <div className="section-card-header" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span className="section-card-title">{weekTitle}</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {!hasPlan && !loading && !futureAccessBlocked && (
                <span className="cell-muted" style={{ fontSize: 12 }}>No plan assigned yet</span>
              )}
              {futureAccessBlocked && !loading && (
                <span className="cell-muted" style={{ fontSize: 12 }}>Next month unlocks when the plan is live and paid</span>
              )}
              {billingPaid && hasPlan && !loading && (
                <button
                  className="btn-xs btn-xs-ghost"
                  onClick={downloadMonthlyPlanPDF}
                  disabled={pdfGenerating}
                  title="Download this month's training plan as a PDF"
                  style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}
                >
                  {pdfGenerating ? 'Generating…' : '↓ Monthly Plan PDF'}
                </button>
              )}
            </div>
          </div>
          <div className="week-nav-bar">
            <button className="week-nav-btn" onClick={() => setWeekOffset(o => o - 1)}>&#8592;</button>
            <span className="week-nav-label">{weekRangeLabel(weekOffset)}</span>
            <button
              className="week-nav-btn"
              onClick={() => setWeekOffset(o => o + 1)}
              disabled={!canGoForward}
              style={{ opacity: !canGoForward ? 0.3 : 1 }}
            >
              &#8594;
            </button>
          </div>
        </div>

        {loading ? (
          <div className="loading-screen" style={{ minHeight: 120 }}>
            <div className="loading-dot-row">
              <div className="loading-dot" /><div className="loading-dot" /><div className="loading-dot" />
            </div>
          </div>
        ) : futureAccessBlocked ? (
          <div className="empty-state" style={{ padding: '32px 24px' }}>
            <p className="empty-state-title">Training Not Available Yet</p>
            <p className="empty-state-sub">
              This month will appear when your coach publishes the plan and your payment is complete.
            </p>
          </div>
        ) : billingLocked ? (
          <div className="billing-lockout">
            <div className="billing-lockout-icon">🔒</div>
            <div className="billing-lockout-title">Training Locked</div>
            <p className="billing-lockout-body">
              Your {billingMonth} coaching payment is overdue. Contact your coach to restore access.
            </p>
            <Link to="/athlete/billing" className="btn btn-primary" style={{ width: 'auto', padding: '10px 28px', display: 'inline-block', textDecoration: 'none' }}>
              View Billing
            </Link>
          </div>
        ) : (
          <>
            {weekWorkouts.length > 0 && (() => {
              const weekRunMiles = weekWorkouts.reduce((s, d) => s + (d.runMiles || 0), 0);
              const weekXtMiles = weekWorkouts.reduce((s, d) => s + (d.xtMiles || 0), 0);
              const weekTotal = weekRunMiles + weekXtMiles;
              const weekHasXt = weekXtMiles > 0.01;
              return (
                <div className="week-volume-total">
                  {weekHasXt ? (
                    <>
                      <span style={{ color: 'var(--navy)' }}>Running: {formatVolume(weekRunMiles) ?? '0 mi'}</span>
                      <span style={{ color: 'var(--gray-400)', margin: '0 6px' }}>·</span>
                      <span style={{ color: 'var(--gray-400)' }}>XT: {formatVolume(weekXtMiles) ?? '0 mi'}</span>
                      <span style={{ margin: '0 6px' }}>·</span>
                      <strong>Week: {formatVolume(weekTotal) ?? '0 mi'}</strong>
                    </>
                  ) : (
                    <>Week total: <strong>{formatVolume(weekTotal) ?? '0 mi'}</strong></>
                  )}
                </div>
              );
            })()}

            {actualWeekMiles > 0.001 && (
              <div className="week-actual-volume">
                <span className="wav-label">Logged</span>
                {actualTodayMiles > 0.001 && (
                  <span>Today: <strong>{formatVolume(actualTodayMiles)}</strong></span>
                )}
                <span>Week: <strong>{formatVolume(actualWeekMiles)}</strong></span>
                {actualMonthMiles > 0.001 && (
                  <span>Month: <strong>{formatVolume(actualMonthMiles)}</strong></span>
                )}
              </div>
            )}

            <div className="week-grid">
              {weekWorkouts.map(day => {
                const log = trainingLogs[day.date];
                const isDragOver = dragOverDate === day.date;
                const isAmPmDay = /\bam\s*:/i.test(day.workout) && /\bpm\s*:/i.test(day.workout);
                const amDone = !!trainingLogSessions[day.date]?.am;
                const pmDone = !!trainingLogSessions[day.date]?.pm;
                const logState = log
                  ? (isAmPmDay ? (amDone && pmDone ? 'full' : 'partial') : 'full')
                  : null;
                return (
                  <div
                    key={day.date}
                    className={`week-day-card${day.isToday ? ' week-day-card--today' : ''}${isDragOver ? ' week-day-card--drag-over' : ''}${logState === 'full' ? ' week-day-card--logged' : ''}${logState === 'partial' ? ' week-day-card--partial' : ''}`}
                    draggable={hasPlan}
                    onDragStart={() => { dragDateRef.current = day.date; }}
                    onDragOver={e => { e.preventDefault(); setDragOverDate(day.date); }}
                    onDragLeave={() => setDragOverDate(null)}
                    onDrop={e => {
                      e.preventDefault();
                      setDragOverDate(null);
                      if (dragDateRef.current && dragDateRef.current !== day.date) {
                        handleSwapDays(dragDateRef.current, day.date);
                      }
                      dragDateRef.current = null;
                    }}
                  >
                    <div className="week-day-name">{day.dayLabel}</div>
                    <div className="week-day-date">{day.dayNum}</div>
                    {day.swappedFromDate && (
                      <div className="day-swapped-badge" title={`Moved from ${day.swappedFromDate}`}>
                        ↔ Moved
                      </div>
                    )}
                    {day.athleteRest && !log && (
                      <div className="day-rest-badge">Rest Day</div>
                    )}

                    {day.race && (
                      <div className="day-race-badge" title={day.race.name}>
                        <span className="day-race-flag">🏁</span>
                        <span className="day-race-name">{day.race.name}</span>
                        {(() => {
                          const evts = parseRaceEvents(day.race.event);
                          if (evts.length > 1) {
                            return evts.map((ek, ei) => {
                              const t = weekRaceResults[`${day.race.id}-${ek}`] ?? (ei === 0 && day.race.result_time_seconds ? formatTime(day.race.result_time_seconds) : null);
                              return t ? <span key={ek} className="day-race-result">{(EVENTS[ek]?.label ?? ek)}: {t}</span> : null;
                            });
                          }
                          return day.race.result_time_seconds > 0 ? <span className="day-race-result">{formatTime(day.race.result_time_seconds)}</span> : null;
                        })()}
                      </div>
                    )}

                    {log && (() => {
                      const isAmPm = /\bam\s*:/i.test(day.workout) && /\bpm\s*:/i.test(day.workout);
                      const amLogged = !!trainingLogSessions[day.date]?.am;
                      const pmLogged = !!trainingLogSessions[day.date]?.pm;
                      const fullyLogged = !isAmPm || (amLogged && pmLogged);
                      const partiallyLogged = isAmPm && (amLogged || pmLogged) && !(amLogged && pmLogged);
                      const missingSession = partiallyLogged ? (amLogged ? 'PM' : 'AM') : null;
                      return (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, flexWrap: 'wrap' }}>
                          {fullyLogged ? (
                            <div className="day-logged-badge" style={{ marginBottom: 0 }}>✓ Logged</div>
                          ) : (
                            <div className="day-logged-badge day-logged-badge--partial" style={{ marginBottom: 0 }}>
                              ½ Logged
                            </div>
                          )}
                          {partiallyLogged && (
                            <button
                              className="day-log-btn day-log-btn--missing"
                              onClick={() => setLogModalDay({ ...day, startSession: missingSession.toLowerCase() })}
                            >
                              + Log {missingSession}
                            </button>
                          )}
                          {(log.running_miles || 0) > 0.001 && (log.xt_miles || 0) > 0.001 ? (
                            <>
                              <span style={{ fontSize: 11, color: 'var(--navy)' }}>Run: {formatVolume(log.running_miles)}</span>
                              <span style={{ fontSize: 11, color: 'var(--gray-400)' }}>XT: {formatVolume(log.xt_miles)}</span>
                              <div className="day-actual-miles">{formatVolume(log.total_miles)}</div>
                            </>
                          ) : (log.xt_miles || 0) > 0.001 && (log.running_miles || 0) <= 0.001 ? (
                            <div className="day-actual-miles" style={{ color: 'var(--gray-400)' }}>XT: {formatVolume(log.xt_miles)}</div>
                          ) : (log.total_miles || 0) > 0.001 ? (
                            <div className="day-actual-miles">{formatVolume(log.total_miles)}</div>
                          ) : null}
                        </div>
                      );
                    })()}

                    <div className="week-day-workout">
                      {log
                        ? (log.segments ?? []).map(s => formatSegmentActual(s)).filter(Boolean).join(' · ') || 'Workout'
                        : day.workout ? renderWorkoutText(day.workout) : <span className="cell-muted">Rest</span>
                      }
                    </div>

                    {(() => {
                      const total = (day.runMiles || 0) + (day.xtMiles || 0);
                      if (total < 0.01) return null;
                      const hasXt = (day.xtMiles || 0) > 0.01;
                      if (!hasXt) return (
                        <div className="day-volume"><strong>{formatVolume(total)}</strong></div>
                      );
                      const xtLabel = day.xtRange ? formatVolumeRange(day.xtRange) : formatVolume(day.xtMiles);
                      return (
                        <div className="day-volume" style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {(day.runMiles || 0) > 0.01 && <span style={{ color: 'var(--navy)', fontSize: 11 }}>Run: {formatVolume(day.runMiles)}</span>}
                          <span style={{ color: 'var(--gray-400)', fontSize: 11 }}>XT: {xtLabel}</span>
                          {!day.xtRange && <strong style={{ fontSize: 12 }}>{formatVolume(total)}</strong>}
                        </div>
                      );
                    })()}

                    {day.coachNote && (
                      <div className="day-coach-note">
                        <span className="day-coach-note-label">Coach</span>
                        {day.coachNote}
                      </div>
                    )}

                    {day.date <= todayStr && (() => {
                      const isAmPm = /\bam\s*:/i.test(day.workout) && /\bpm\s*:/i.test(day.workout);
                      if (isAmPm) {
                        const amLogged = !!trainingLogSessions[day.date]?.am;
                        const pmLogged = !!trainingLogSessions[day.date]?.pm;
                        return (
                          <div style={{ display: 'flex', gap: 4, flexDirection: 'column', marginTop: 4 }}>
                            <button className="day-log-btn" onClick={() => setLogModalDay({ ...day, startSession: 'am' })}>
                              {amLogged ? '✓ Edit AM' : '+ Log AM'}
                            </button>
                            <button className="day-log-btn" style={pmLogged ? {} : { opacity: 0.75 }} onClick={() => setLogModalDay({ ...day, startSession: 'pm' })}>
                              {pmLogged ? '✓ Edit PM' : '+ Log PM'}
                            </button>
                          </div>
                        );
                      }
                      return (
                        <button className="day-log-btn" onClick={() => setLogModalDay(day)}>
                          {log ? 'Edit Log' : '+ Log'}
                        </button>
                      );
                    })()}

                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* ── Training Volume ──────────────────────────────── */}
      {(() => {
        const thisWeekMiles = weekBars[15]?.miles ?? 0;
        const prevWeeks = weekBars.slice(3, 15).map(w => w.miles);
        const avgMiles = prevWeeks.length ? prevWeeks.reduce((a, b) => a + b, 0) / prevWeeks.length : 0;
        const trend = avgMiles > 0.5 ? Math.round(((thisWeekMiles - avgMiles) / avgMiles) * 100) : null;
        const gridLines = [0.25, 0.5, 0.75, 1].map(pct => Math.round(maxBarMiles * pct * 10) / 10);
        return (
          <div className="section-card" style={{ marginTop: 16, marginBottom: 16 }}>
            <div className="vol-header">
              <span className="section-card-title" style={{ marginBottom: 0 }}>Training Volume</span>
              <div className="vol-stats-row">
                <div className="vol-stat">
                  <span className="vol-stat-value">{thisWeekMiles > 0.01 ? (Math.round(thisWeekMiles * 10) / 10) + ' mi' : '—'}</span>
                  <span className="vol-stat-label">This Week</span>
                </div>
                <div className="vol-stat-divider" />
                <div className="vol-stat">
                  <span className="vol-stat-value">{avgMiles > 0.5 ? (Math.round(avgMiles * 10) / 10) + ' mi' : '—'}</span>
                  <span className="vol-stat-label">12-wk Avg</span>
                </div>
                {trend !== null && (
                  <>
                    <div className="vol-stat-divider" />
                    <div className="vol-stat">
                      <span className={`vol-stat-value${trend >= 0 ? ' vol-trend-up' : ' vol-trend-down'}`}>
                        {trend >= 0 ? '↑' : '↓'} {Math.abs(trend)}%
                      </span>
                      <span className="vol-stat-label">vs Avg</span>
                    </div>
                  </>
                )}
              </div>
            </div>
            {historyLogs.length === 0 ? (
              <p className="cell-muted" style={{ padding: '16px 24px', fontSize: 13 }}>Log workouts to see your training volume here.</p>
            ) : (
              <div className="vol-chart-wrap">
                <div className="vol-chart">
                  {/* Grid lines */}
                  <div className="vol-grid">
                    {gridLines.map((val, gi) => (
                      <div key={gi} className="vol-grid-line" style={{ bottom: `${(val / maxBarMiles) * 100}%` }}>
                        <span className="vol-grid-label">{val}</span>
                      </div>
                    ))}
                  </div>
                  {/* Bars */}
                  <div className="vol-bars">
                    {weekBars.map((bar, i) => {
                      const isCurrent = i === 15;
                      const heightPct = bar.miles > 0.01 ? Math.max(2, (bar.miles / maxBarMiles) * 100) : 0;
                      return (
                        <div key={i} className="vol-bar-col">
                          <div className="vol-bar-wrap">
                            {bar.miles > 0.01 && (
                              <div className={`vol-bar-tooltip${isCurrent ? ' vol-bar-tooltip--current' : ''}`}>
                                {Math.round(bar.miles * 10) / 10} mi
                              </div>
                            )}
                            <div
                              className={`vol-bar${isCurrent ? ' vol-bar--current' : ''}`}
                              style={{ height: `${heightPct}%` }}
                            />
                          </div>
                          <div className={`vol-bar-label${isCurrent ? ' vol-bar-label--current' : ''}`}>{bar.label}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* ── Best times ───────────────────────────────────── */}
      {Object.keys(bestSplits).length > 0 && (
        <div className="section-card" style={{ marginTop: 16, marginBottom: 16 }}>
          <div className="section-card-header">
            <span className="section-card-title">Best Times</span>
            <span className="cell-muted" style={{ fontSize: 12 }}>From training logs (last 8 weeks)</span>
          </div>
          <div className="best-splits-grid">
            {Object.entries(bestSplits)
              .sort(([a], [b]) => a.localeCompare(b, undefined, { numeric: true }))
              .map(([dist, record]) => (
                <div key={dist} className="best-split-item">
                  <div className="best-split-dist">{dist}</div>
                  <div className="best-split-time">{record.timeStr}</div>
                  <div className="best-split-date">{formatShortDate(record.logDate)}</div>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* ── Pending race results ─────────────────────────── */}
      {pendingResults.length > 0 && (
        <div className="section-card" style={{ marginBottom: 20, borderLeft: '3px solid var(--gold)' }}>
          <div className="section-card-header">
            <span className="section-card-title">Log Race Results</span>
          </div>
          <div>
            {pendingResults.map(race => (
              <div key={race.id} style={{ padding: '14px 24px', borderBottom: '1px solid var(--gray-100)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <div className={`race-priority-badge priority-${race.priority}`}>{race.priority}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--navy)' }}>{race.name}</div>
                    <div className="cell-muted" style={{ fontSize: 12 }}>
                      {getEventLabels(race.event)} · {formatRaceDate(race.race_date)}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      className="form-input"
                      style={{ width: 100, padding: '6px 10px', fontSize: 13 }}
                      type="text"
                      placeholder={race.event === 'marathon' ? '2:45:00.00' : '4:32.56'}
                      value={resultInputs[race.id] ?? ''}
                      onChange={e => setResultInputs(prev => ({ ...prev, [race.id]: e.target.value }))}
                    />
                    <button
                      className="btn btn-primary"
                      style={{ width: 'auto', padding: '7px 16px', fontSize: 13 }}
                      onClick={() => handleLogResult(race)}
                      disabled={resultSaving === race.id}
                    >
                      {resultSaving === race.id ? 'Saving...' : 'Log'}
                    </button>
                  </div>
                </div>
                {resultErrors[race.id] && (
                  <p style={{ margin: '4px 0 0 44px', fontSize: 12, color: 'var(--red, #dc2626)' }}>{resultErrors[race.id]}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Travel / Vacation ────────────────────────────── */}
      {(() => {
        const EQUIPMENT_OPTIONS = [
          { key: 'track', label: 'Track' },
          { key: 'treadmill', label: 'Treadmill' },
          { key: 'roads', label: 'Roads / Trails' },
          { key: 'weight_room', label: 'Weight Room' },
          { key: 'pool', label: 'Pool' },
          { key: 'gym', label: 'Gym' },
        ];

        // Compute all dates in the selected range for blackout checkboxes
        function datesInRange(start, end) {
          if (!start || !end || end < start) return [];
          const dates = [];
          const d = new Date(start + 'T00:00:00');
          const last = new Date(end + 'T00:00:00');
          while (d <= last) {
            dates.push(d.toISOString().slice(0, 10));
            d.setDate(d.getDate() + 1);
          }
          return dates;
        }

        function toggleEquipment(key) {
          setVacEquipment(prev =>
            prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
          );
        }

        function toggleBlackout(dateStr) {
          setVacBlackouts(prev =>
            prev.includes(dateStr) ? prev.filter(d => d !== dateStr) : [...prev, dateStr]
          );
        }

        function fmtVacDate(dateStr) {
          return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        }

        const rangeForForm = datesInRange(vacStart, vacEnd);

        return (
          <div className="section-card" style={{ marginBottom: 20 }}>
            <div className="section-card-header">
              <span className="section-card-title">Travel / Vacation</span>
              <button
                className="btn-xs btn-xs-ghost"
                onClick={() => { setShowVacForm(v => !v); setVacError(''); }}
              >
                {showVacForm ? 'Cancel' : '+ Add Trip'}
              </button>
            </div>

            {showVacForm && (
              <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--gray-100)' }}>
                <form onSubmit={handleAddVacation}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label">Start Date</label>
                      <input className="form-input" type="date" value={vacStart} onChange={e => { setVacStart(e.target.value); setVacBlackouts([]); }} required />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label">End Date</label>
                      <input className="form-input" type="date" value={vacEnd} min={vacStart} onChange={e => { setVacEnd(e.target.value); setVacBlackouts([]); }} required />
                    </div>
                  </div>

                  <div className="form-group" style={{ marginBottom: 12 }}>
                    <label className="form-label">What will you have access to?</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
                      {EQUIPMENT_OPTIONS.map(opt => (
                        <label key={opt.key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={vacEquipment.includes(opt.key)}
                            onChange={() => toggleEquipment(opt.key)}
                          />
                          {opt.label}
                        </label>
                      ))}
                    </div>
                  </div>

                  {rangeForForm.length > 1 && (
                    <div className="form-group" style={{ marginBottom: 12 }}>
                      <label className="form-label">Days you can't run at all</label>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
                        {rangeForForm.map(dateStr => (
                          <label key={dateStr} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={vacBlackouts.includes(dateStr)}
                              onChange={() => toggleBlackout(dateStr)}
                            />
                            {fmtVacDate(dateStr)}
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="form-group" style={{ marginBottom: 12 }}>
                    <label className="form-label">Notes (optional)</label>
                    <input className="form-input" type="text" value={vacNotes} onChange={e => setVacNotes(e.target.value)} placeholder="Hotel gym only, no long runs, etc." />
                  </div>

                  {vacError && <p className="form-error" style={{ marginTop: 0, marginBottom: 8 }}>{vacError}</p>}
                  <button className="btn btn-primary" type="submit" disabled={vacSaving} style={{ width: 'auto', padding: '8px 20px' }}>
                    {vacSaving ? 'Saving...' : 'Save Trip'}
                  </button>
                </form>
              </div>
            )}

            {vacations.length === 0 && !showVacForm ? (
              <div className="section-card-body" style={{ color: 'var(--gray-400)', fontSize: 13 }}>
                No upcoming trips. Click "+ Add Trip" to let your coach know about travel.
              </div>
            ) : (
              <div style={{ padding: vacations.length > 0 ? '8px 0' : 0 }}>
                {vacations.map(v => {
                  const equipLabels = (v.available_equipment ?? []).map(k => EQUIPMENT_OPTIONS.find(o => o.key === k)?.label ?? k);
                  const blackoutLabels = (v.blackout_dates ?? []).sort().map(fmtVacDate);
                  return (
                    <div key={v.id} className="vac-item">
                      <div className="vac-item-dates">
                        {fmtVacDate(v.start_date)}
                        {v.start_date !== v.end_date && ` – ${fmtVacDate(v.end_date)}`}
                      </div>
                      <div className="vac-item-body">
                        {equipLabels.length > 0 && (
                          <div className="vac-item-row">
                            <span className="vac-item-label">Available:</span>
                            {equipLabels.join(', ')}
                          </div>
                        )}
                        {blackoutLabels.length > 0 && (
                          <div className="vac-item-row">
                            <span className="vac-item-label">No-run days:</span>
                            {blackoutLabels.join(', ')}
                          </div>
                        )}
                        {v.notes && <div className="vac-item-notes">"{v.notes}"</div>}
                      </div>
                      <button
                        className="vac-item-delete"
                        onClick={() => handleDeleteVacation(v.id)}
                        title="Remove"
                      >×</button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })()}


      {/* ── Parents / Guardians ──────────────────────────── */}
      <div className="section-card" style={{ marginTop: 20 }}>
        <div className="section-card-header">
          <span className="section-card-title">Parents / Guardians</span>
        </div>
        <div style={{ padding: '16px 24px' }}>
          <form onSubmit={handleAddParent} style={{ display: 'flex', gap: 8, marginBottom: parents.length > 0 ? 16 : 0 }}>
            <input
              className="form-input"
              type="email"
              placeholder="Parent's email address"
              value={parentEmail}
              onChange={e => setParentEmail(e.target.value)}
              required
              style={{ flex: 1 }}
            />
            <button className="btn btn-primary" type="submit" disabled={parentSaving} style={{ width: 'auto', padding: '10px 20px' }}>
              {parentSaving ? 'Linking...' : 'Link Parent'}
            </button>
          </form>
          {parentError && <p className="form-error" style={{ marginTop: 8 }}>{parentError}</p>}
          {parents.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {parents.map(p => (
                <div key={p.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: 'var(--gray-50)', borderRadius: 8 }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--navy)' }}>{p.profiles?.full_name}</div>
                    <div style={{ fontSize: 12, color: 'var(--gray-400)' }}>{p.profiles?.email}</div>
                  </div>
                  <button
                    onClick={() => handleRemoveParent(p.id)}
                    style={{ background: 'none', border: 'none', color: 'var(--gray-400)', cursor: 'pointer', fontSize: 18, lineHeight: 1 }}
                    title="Remove"
                  >×</button>
                </div>
              ))}
            </div>
          )}
          {parents.length === 0 && !parentError && (
            <p style={{ fontSize: 13, color: 'var(--gray-400)', marginTop: 8 }}>No parents linked yet.</p>
          )}
        </div>
      </div>

      {/* ── Training log modal ───────────────────────────── */}
      {logModalDay && (
        <TrainingLogModal
          day={logModalDay}
          existingLog={trainingLogSessions[logModalDay.date]?.full ?? null}
          existingAmLog={trainingLogSessions[logModalDay.date]?.am ?? null}
          existingPmLog={trainingLogSessions[logModalDay.date]?.pm ?? null}
          startSession={logModalDay.startSession}
          paces={paces}
          onSave={handleLogSaved}
          onClose={() => setLogModalDay(null)}
          onRestDay={handleRestDay}
          initialAthleteRest={workoutPlanMap[logModalDay.date]?.athleteRest ?? false}
        />
      )}

      {/* ── Athlete onboarding modal ──────────────────────── */}
      {showBaselineModal && (
        <div className="day-modal-overlay">
          <div className="day-modal" style={{ maxWidth: 520 }}>
            <div className="day-modal-header">
              <div>
                <div className="day-modal-date">Welcome to Apex Elite</div>
                <div style={{ fontSize: 13, color: 'var(--gray-400)', marginTop: 2 }}>
                  Step {onbStep} of 3 — {onbStep === 1 ? 'About You' : onbStep === 2 ? 'School & Events' : 'Baseline Results'}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                {[1,2,3].map(s => (
                  <div key={s} style={{ width: 8, height: 8, borderRadius: '50%', background: s <= onbStep ? 'var(--navy)' : 'var(--gray-200)' }} />
                ))}
              </div>
            </div>

            <div className="day-modal-body" style={{ maxHeight: '65vh', overflowY: 'auto' }}>

              {/* Step 1 — Personal Info */}
              {onbStep === 1 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    <div className="form-group" style={{ marginBottom: 0, flex: '1 1 160px' }}>
                      <label className="form-label">Gender</label>
                      <select className="form-input form-select" value={onbGender} onChange={e => setOnbGender(e.target.value)}>
                        <option value="">— Select —</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="other">Other / Prefer not to say</option>
                      </select>
                    </div>
                    <div className="form-group" style={{ marginBottom: 0, flex: '1 1 160px' }}>
                      <label className="form-label">Date of Birth</label>
                      <input className="form-input" type="date" value={onbBirthDate} onChange={e => setOnbBirthDate(e.target.value)} />
                    </div>
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Phone Number</label>
                    <input className="form-input" type="tel" placeholder="(555) 123-4567" value={onbPhone} onChange={e => setOnbPhone(e.target.value)} />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Address</label>
                    <input className="form-input" type="text" placeholder="123 Main St, City, State, ZIP" value={onbAddress} onChange={e => setOnbAddress(e.target.value)} />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0, maxWidth: 180 }}>
                    <label className="form-label">Shirt Size</label>
                    <select className="form-input form-select" value={onbShirtSize} onChange={e => setOnbShirtSize(e.target.value)}>
                      <option value="">— Select —</option>
                      {['XS','S','M','L','XL','2XL','3XL'].map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
              )}

              {/* Step 2 — School & Events */}
              {onbStep === 2 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Grade / Level</label>
                    <select className="form-input form-select" value={onbGradeLevel} onChange={e => { setOnbGradeLevel(e.target.value); setOnbSchoolName(''); }}>
                      <option value="">— Select —</option>
                      <optgroup label="Middle School">
                        {['6th','7th','8th'].map(g => <option key={g} value={g}>{g} Grade</option>)}
                      </optgroup>
                      <optgroup label="High School">
                        {['9th','10th','11th','12th'].map(g => <option key={g} value={g}>{g} Grade</option>)}
                      </optgroup>
                      <option value="collegiate">Collegiate</option>
                      <option value="post-collegiate">Post-Collegiate</option>
                    </select>
                  </div>
                  {onbSchoolLabel() && (
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label">{onbSchoolLabel()}</label>
                      <input className="form-input" type="text" placeholder="School name" value={onbSchoolName} onChange={e => setOnbSchoolName(e.target.value)} />
                    </div>
                  )}
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Events You Plan to Run</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 6 }}>
                      {Object.entries(EVENTS)
                        // Relays are race types, not events you plan to
                        // run as a benchmark — splits handle that.
                        .filter(([key]) => !isRelayEvent(key))
                        .map(([key, ev]) => (
                        <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, cursor: 'pointer', padding: '4px 10px', borderRadius: 16, border: '1px solid', borderColor: onbEvents.includes(key) ? 'var(--navy)' : 'var(--gray-200)', background: onbEvents.includes(key) ? 'var(--navy)' : 'transparent', color: onbEvents.includes(key) ? '#fff' : 'var(--gray-700)', userSelect: 'none' }}>
                          <input type="checkbox" style={{ display: 'none' }} checked={onbEvents.includes(key)} onChange={() => toggleOnbEvent(key)} />
                          {ev.label}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3 — Baseline Results */}
              {onbStep === 3 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <p style={{ fontSize: 13, color: 'var(--gray-500)', margin: 0 }}>
                    Enter your existing personal bests. These become your baseline for tracking improvement.
                  </p>
                  {baselineRows.map((row, i) => (
                    <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                      <div className="form-group" style={{ marginBottom: 0, flex: '0 0 150px' }}>
                        {i === 0 && <label className="form-label">Event</label>}
                        <select
                          className="form-input form-select"
                          value={row.event}
                          onChange={e => setBaselineRows(rows => rows.map((r, j) => j === i ? { ...r, event: e.target.value } : r))}
                        >
                          {Object.entries(EVENTS)
                            .filter(([key]) => !isRelayEvent(key))
                            .map(([key, ev]) => (
                            <option key={key} value={key}>{ev.label}</option>
                          ))}
                        </select>
                      </div>
                      {row.event === 'other' && (
                        <div style={{ display: 'flex', gap: 4 }}>
                          <div className="form-group" style={{ marginBottom: 0, flex: '1 1 80px' }}>
                            {i === 0 && <label className="form-label">Distance</label>}
                            <input
                              className="form-input"
                              type="number"
                              min="0"
                              step="0.1"
                              placeholder="e.g. 5.0"
                              value={row.customDist}
                              onChange={e => setBaselineRows(rows => rows.map((r, j) => j === i ? { ...r, customDist: e.target.value } : r))}
                            />
                          </div>
                          <div className="form-group" style={{ marginBottom: 0, flex: '0 0 65px' }}>
                            {i === 0 && <label className="form-label">Unit</label>}
                            <select
                              className="form-input form-select"
                              value={row.customUnit}
                              onChange={e => setBaselineRows(rows => rows.map((r, j) => j === i ? { ...r, customUnit: e.target.value } : r))}
                            >
                              <option value="mi">mi</option>
                              <option value="km">km</option>
                            </select>
                          </div>
                        </div>
                      )}
                      <div className="form-group" style={{ marginBottom: 0, flex: '1 1 90px' }}>
                        {i === 0 && <label className="form-label">Time</label>}
                        <input
                          className="form-input"
                          type="text"
                          placeholder="4:32.56"
                          value={row.time}
                          onChange={e => setBaselineRows(rows => rows.map((r, j) => j === i ? { ...r, time: e.target.value } : r))}
                        />
                      </div>
                      <div className="form-group" style={{ marginBottom: 0, flex: '1 1 130px' }}>
                        {i === 0 && <label className="form-label">Date ran</label>}
                        <input
                          className="form-input"
                          type="date"
                          value={row.date}
                          onChange={e => setBaselineRows(rows => rows.map((r, j) => j === i ? { ...r, date: e.target.value } : r))}
                        />
                      </div>
                      {baselineRows.length > 1 && (
                        <button
                          style={{ background: 'none', border: 'none', color: 'var(--gray-400)', cursor: 'pointer', fontSize: 18, paddingBottom: 6 }}
                          onClick={() => setBaselineRows(rows => rows.filter((_, j) => j !== i))}
                        >×</button>
                      )}
                    </div>
                  ))}
                  <button
                    className="btn-xs btn-xs-ghost"
                    style={{ alignSelf: 'flex-start' }}
                    onClick={() => setBaselineRows(rows => [...rows, { event: 'mile', time: '', date: '', customDist: '', customUnit: 'mi' }])}
                  >
                    + Add another event
                  </button>
                  {baselineError && <p className="form-error" style={{ marginTop: 4 }}>{baselineError}</p>}
                </div>
              )}
            </div>

            <div className="day-modal-footer">
              {onbStep === 1 ? (
                <button className="btn-xs btn-xs-ghost" onClick={handleBaselineSkip}>Skip setup</button>
              ) : (
                <button className="btn-xs btn-xs-ghost" onClick={() => setOnbStep(s => s - 1)}>Back</button>
              )}
              {onbStep < 3 ? (
                <button className="btn-xs btn-xs-primary" onClick={() => setOnbStep(s => s + 1)}>
                  Next
                </button>
              ) : (
                <button className="btn-xs btn-xs-primary" onClick={handleBaselineSubmit} disabled={baselineSaving}>
                  {baselineSaving ? 'Saving...' : 'Finish'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
